/*
 * Every piece of brand identity in one place.
 *
 * The name has to stretch across three things — the simulator, the newsletter,
 * and a possible future tax product — and it appears in places that are annoying
 * to chase later: the share card F2 renders to canvas, the co-branded creator
 * card in F10, the newsletter footer, the page title, the storage keys.
 *
 * Deliberately a plain block comment, not JSDoc: a doc comment here would attach
 * to whatever declaration follows and steal BRAND's own hover text.
 */

/** Declared once so `url` and `shareUrl` below cannot drift from the domain. */
const DOMAIN = 'glidepath.co.in'

/**
 * One handle across Instagram, Reddit and X. If a platform's handle ever differs
 * — or a presence is a subreddit rather than a user — split it out rather than
 * bending this one.
 */
const HANDLE = 'glidepathindia'

/**
 * Brand identity. Anything that names the product imports from here rather than
 * hardcoding a string.
 */
export const BRAND = {
  /**
   * Full name, as written in prose, on the share card, and as the wordmark.
   *
   * The wordmark is a single word in a single colour — no two-tone split. Note
   * that the name no longer carries "India", so the description below is now the
   * only thing that says who this is calibrated for. Keep that in it.
   */
  name: 'Glidepath',

  domain: DOMAIN,
  url: `https://${DOMAIN}`,
  /**
   * What gets stamped on the F2 share card: the bare domain, no scheme — short
   * enough to stay legible small. Derived, not a second literal, so it cannot
   * disagree with `domain` on the next domain change.
   */
  shareUrl: DOMAIN,

  handle: HANDLE,
  instagramUrl: `https://instagram.com/${HANDLE}`,
  redditUrl: `https://reddit.com/user/${HANDLE}`,
  xUrl: `https://x.com/${HANDLE}`,

  /**
   * Where someone writes to get off the monthly-note list.
   *
   * This is a compliance surface, not a nicety. Consent to be emailed has to be
   * withdrawable, and with no newsletter provider yet there is no unsubscribe
   * link to offer — so this address IS the withdrawal route, and it is printed
   * at the point of collection rather than buried in a policy.
   *
   * It must resolve to a mailbox someone reads. An address that bounces is
   * worse than no address, because the copy beside it promises a person on the
   * other end.
   */
  supportEmail: `hello@${DOMAIN}`,

  tagline: 'Will you actually reach FIRE?',
  /* Kept in step with index.html's meta description and with App.tsx's footer,
     all three of which make the same promise and must scope it the same way. The
     plan travels in the URL fragment, so an unqualified "never leave your device"
     is false the moment a link is shared — the reasoning is spelled out in App.tsx's
     footer comment, which opens "The privacy line is first". MAINT-03 fixed the two rendered copies and found this one,
     the declared source of truth, still carrying the absolute version. Nothing
     reads it today, which is exactly why it went unnoticed. */
  description:
    'Probabilistic FIRE projections calibrated to India. 10,000 Monte Carlo simulations, ' +
    'every one of them run in your browser. Your amounts stay on your device unless you ' +
    'share the link.',
} as const

/**
 * Namespace for anything written to localStorage. Prefixed so a future product
 * on the same origin cannot collide with it.
 *
 * NOTE: `index.html`'s pre-paint script cannot import this module, so it
 * hardcodes the resolved `glidepath.theme` key. Change the prefix here and you
 * must change it there too, or the toggle writes a key the pre-paint script
 * never reads and the flash-of-wrong-mode returns. It also migrates the two
 * pre-rename keys, `corpusindia.theme` and `corpus.theme` — drop those once no
 * user could plausibly still hold one.
 */
export const STORAGE_PREFIX = 'glidepath'

export const storageKey = (key: string): string => `${STORAGE_PREFIX}.${key}`

/**
 * The browser-chrome colour per theme, for the `theme-color` meta tag.
 *
 * These are `--c-bg-base` from `src/styles/tokens.css` — what `body` actually
 * paints — resolved from OKLCH to sRGB. They must stay in step with that token,
 * otherwise mobile Safari/Chrome paint the address bar a different colour from
 * the page it sits above. The same two literals appear in `index.html`, which
 * needs them before any stylesheet has loaded and so cannot read the token.
 */
export const THEME_COLOR = {
  dark: '#0d1117',
  light: '#f9fafc',
} as const
